import { createContext, useContext, useMemo } from "react";

import { UseQueryResult } from "@tanstack/react-query";

import { useGetPinnedJobs } from "@/hooks/queries/useGetPinnedJobs";
import { useUpdateSetPinnedJob } from "@/hooks/updateSets/useUpdateSetPinnedJob";
import { PinnedJobResponse } from "@/types";

interface PinnedJobsContextValue {
  queryResult: UseQueryResult<PinnedJobResponse[]>;
  pinnedJobIds: Set<string>;
  isPinned: (jobId: string) => boolean;
  togglePin: (jobId: string) => void;
}

const PinnedJobsContext = createContext<PinnedJobsContextValue | undefined>(
  undefined,
);

export const PinnedJobsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const queryResult = useGetPinnedJobs();
  const { mutate: updatePinnedJob } = useUpdateSetPinnedJob();

  const pinnedJobIds = useMemo(
    () => new Set((queryResult.data ?? []).map((job) => job.jobId)),
    [queryResult.data],
  );

  const isPinned = (jobId: string) => pinnedJobIds.has(jobId);

  const togglePin = (jobId: string) => {
    updatePinnedJob({ jobId, pinned: !pinnedJobIds.has(jobId) });
  };

  return (
    <PinnedJobsContext.Provider
      value={{ queryResult, pinnedJobIds, isPinned, togglePin }}
    >
      {children}
    </PinnedJobsContext.Provider>
  );
};

export const usePinnedJobs = () => {
  const context = useContext(PinnedJobsContext);
  if (!context) {
    throw new Error("usePinnedJobs must be used within a PinnedJobsProvider");
  }
  return context;
};
